import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ImagePlus, Lock, X } from 'lucide-react';
import { ApiError } from '@signalvote/api-client';
import {
  ErrorCode,
  WORK_ORDER_IMAGE_TYPES,
  WORK_ORDER_MAX_IMAGE_BYTES,
  WORK_ORDER_MAX_MESSAGE_CHARS,
  WORK_ORDER_OPT_OUT_LINE,
  assembleTextSendMessage,
} from '@signalvote/shared';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/** What voters receive: the text as the vendor sends it, plus the image. */
export interface TextSendContent {
  body: string;
  paidForDisclaimer: string;
  imageUrl: string | null;
}

/** Image refusals in plain words, by their stable code. */
const IMAGE_REFUSALS: Partial<Record<string, string>> = {
  [ErrorCode.ConcurrencyConflict]: 'Someone else changed this text. Refresh to see it, then add the image again.',
  [ErrorCode.ImageAttachRequiresDraft]: 'This text is already on its way, so its image can’t change.',
};

export function imageUploadErrorMessage(error: unknown): string | null {
  if (!error) return null;
  const refusal = error instanceof ApiError && error.code ? IMAGE_REFUSALS[error.code] : undefined;
  return refusal ?? 'We couldn’t upload that image. Try again.';
}

/** The disclaimer a campaign starts from: its Paid for by, or nothing to prefill. */
export function disclaimerFromPaidForBy(paidForBy: string | null | undefined): string {
  return paidForBy?.trim() ? `Paid for by ${paidForBy.trim()}` : '';
}

/** Why a Text send can't go out as written yet, or null when it can. */
export function textSendProblem({ body, paidForDisclaimer }: Pick<TextSendContent, 'body' | 'paidForDisclaimer'>) {
  if (!body.trim()) return 'Write your message.';
  if (!paidForDisclaimer.trim()) return 'Add who paid for this text.';
  if (assembleTextSendMessage(body, paidForDisclaimer).length > WORK_ORDER_MAX_MESSAGE_CHARS) {
    return 'Shorten your text to fit.';
  }
  return null;
}

function imageProblem(file: File): string | null {
  if (!WORK_ORDER_IMAGE_TYPES.includes(file.type)) return 'Use a JPG, PNG or GIF image.';
  if (file.size > WORK_ORDER_MAX_IMAGE_BYTES) {
    return `That image is over ${WORK_ORDER_MAX_IMAGE_BYTES / 1024 / 1024} MB. Use a smaller one.`;
  }
  return null;
}

/** A local image as a URL the page can show, released when it changes. */
export function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

export interface TextComposerProps {
  body: string;
  paidForDisclaimer: string;
  imageUrl: string | null;
  onBodyChange: (body: string) => void;
  onDisclaimerChange: (disclaimer: string) => void;
  /** A field lost focus: the moment to save. */
  onBlur?: () => void;
  /** The person reached into the message (a key, a paste, a tap), so anything writing into it should stop. */
  onBodyInterrupt?: () => void;
  /** A JPG, PNG or GIF within the size limit; anything else is refused here with a reason. */
  onImagePick?: (file: File) => void;
  /** Absent where an attached image can only be replaced. */
  onImageRemove?: () => void;
  imageBusy?: boolean;
  imageError?: string | null;
  autoFocus?: boolean;
}

/**
 * Writes a Text send: the message, an optional image, the Paid-for disclaimer and the fixed Opt-out
 * line. The count is the whole text as voters get it, held to the 2,000-character limit.
 */
export function TextComposer({
  body,
  paidForDisclaimer,
  imageUrl,
  onBodyChange,
  onDisclaimerChange,
  onBlur,
  onBodyInterrupt,
  onImagePick,
  onImageRemove,
  imageBusy = false,
  imageError = null,
  autoFocus = false,
}: TextComposerProps) {
  const id = useId();
  const [pickError, setPickError] = useState<string | null>(null);
  // An empty disclaimer is only called out once the person has left the field, never on arrival.
  const [leftDisclaimer, setLeftDisclaimer] = useState(false);
  const disclaimerMissing = leftDisclaimer && !paidForDisclaimer.trim();
  const length = assembleTextSendMessage(body, paidForDisclaimer).length;
  const overLimit = length > WORK_ORDER_MAX_MESSAGE_CHARS;

  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires.
    e.target.value = '';
    if (!file) return;
    const problem = imageProblem(file);
    setPickError(problem);
    if (!problem) onImagePick?.(file);
  };
  const imageAlert = pickError ?? imageError;

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-body`}>Message</Label>
        <textarea
          id={`${id}-body`}
          rows={6}
          autoFocus={autoFocus}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          onKeyDown={onBodyInterrupt}
          onPaste={onBodyInterrupt}
          onPointerDown={onBodyInterrupt}
          onBlur={onBlur}
          placeholder="Early voting starts Monday. Find your polling place at…"
          className="w-full resize-y rounded-xl border border-input bg-transparent px-4 py-3 text-[1.0625rem] leading-relaxed transition-colors focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
        />
        <p className="flex justify-end text-xs text-muted-foreground">
          <span
            data-testid="char-count"
            data-over-limit={overLimit}
            className={cn('tabular-nums', overLimit && 'font-semibold text-destructive')}
          >
            {length.toLocaleString('en-US')} / {WORK_ORDER_MAX_MESSAGE_CHARS.toLocaleString('en-US')}
          </span>
        </p>
        {overLimit && (
          <p className="text-sm text-destructive">
            Your text is over {WORK_ORDER_MAX_MESSAGE_CHARS.toLocaleString('en-US')} characters, counting the disclaimer and
            the STOP line. Shorten it to continue.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          {imageUrl && (
            <img data-testid="image-preview" src={imageUrl} alt="Your image" className="h-14 w-14 rounded-md border object-cover" />
          )}
          <label
            htmlFor={`${id}-image`}
            className={cn(
              'inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent focus-within:ring-2 focus-within:ring-ring',
              imageBusy && 'pointer-events-none opacity-50',
            )}
          >
            <ImagePlus className="h-4 w-4" aria-hidden="true" />
            {imageUrl ? 'Replace image' : 'Add an image'}
            <span className="font-normal text-muted-foreground">(optional)</span>
            <input
              id={`${id}-image`}
              type="file"
              accept={WORK_ORDER_IMAGE_TYPES.join(',')}
              disabled={imageBusy}
              onChange={pick}
              className="sr-only"
            />
          </label>
          {imageUrl && onImageRemove && (
            <Button type="button" variant="ghost" size="sm" onClick={onImageRemove}>
              <X aria-hidden="true" /> Remove image
            </Button>
          )}
        </div>
        {imageBusy && (
          <p role="status" className="text-sm text-muted-foreground">
            Uploading image…
          </p>
        )}
        {imageAlert && (
          <p role="alert" className="text-sm text-destructive">
            {imageAlert}
          </p>
        )}
      </div>

      <fieldset className="space-y-3 rounded-xl border border-input p-4">
        <legend className="px-1 text-sm font-medium text-foreground">Added to every text</legend>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-disclaimer`}>Paid-for disclaimer</Label>
          <Input
            id={`${id}-disclaimer`}
            value={paidForDisclaimer}
            onChange={(e) => onDisclaimerChange(e.target.value)}
            onBlur={() => {
              setLeftDisclaimer(true);
              onBlur?.();
            }}
            placeholder="Paid for by Smith for Mayor"
            aria-invalid={disclaimerMissing || undefined}
            aria-describedby={cn(disclaimerMissing && `${id}-disclaimer-missing`, `${id}-disclaimer-why`)}
            className={cn(disclaimerMissing && 'border-destructive focus-visible:border-destructive')}
          />
          {disclaimerMissing && (
            <p id={`${id}-disclaimer-missing`} className="text-sm text-destructive">
              Add who paid for this text.
            </p>
          )}
          <p id={`${id}-disclaimer-why`} className="text-xs text-muted-foreground">
            Every political text has to say who paid for it, usually your committee’s name as filed.
          </p>
        </div>
        <div
          data-testid="opt-out-line"
          className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm text-foreground"
        >
          <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">“{WORK_ORDER_OPT_OUT_LINE}”</span>
          <span className="ml-auto text-xs text-muted-foreground">Always added</span>
        </div>
      </fieldset>
    </div>
  );
}

const PHONE_SIZES = {
  compact: { width: 'max-w-[240px]', screen: 'h-[300px]' },
  regular: { width: 'max-w-[280px]', screen: 'h-[380px]' },
  large: { width: 'max-w-[330px]', screen: 'h-[500px]' },
} as const;

export type PhonePreviewSize = keyof typeof PHONE_SIZES;

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export interface PhonePreviewProps extends TextSendContent {
  size?: PhonePreviewSize;
  /** Something is typing into the message: a caret blinks at its end. */
  typing?: boolean;
}

/**
 * A phone showing the text exactly as voters receive it. Only the first real text after the placeholder
 * settles in; nothing moves per keystroke.
 */
export function PhonePreview({ body, paidForDisclaimer, imageUrl, size = 'regular', typing = false }: PhonePreviewProps) {
  const hasText = body.trim() !== '';
  return (
    <div
      data-testid="phone-preview"
      className={cn(
        'mx-auto w-full rounded-[2rem] border-[6px] border-foreground/90 bg-background shadow-xl',
        PHONE_SIZES[size].width,
      )}
    >
      <div className="flex justify-center border-b py-2">
        <span className="h-1.5 w-10 rounded-full bg-foreground/80" />
      </div>
      <div className={cn('space-y-2 overflow-y-auto p-3', PHONE_SIZES[size].screen)}>
        <p className="text-center text-[10px] text-muted-foreground">Text Message</p>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={hasText ? 'text' : 'placeholder'}
            initial={hasText ? { opacity: 0, scale: 0.96 } : false}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, transition: { duration: 0 } }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            style={{ transformOrigin: 'bottom left' }}
            className="max-w-[88%] space-y-1 rounded-2xl rounded-bl-md bg-muted p-2"
          >
            {imageUrl && (
              <motion.img
                key={imageUrl}
                src={imageUrl}
                alt=""
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: EASE_OUT }}
                className="w-full rounded-xl"
              />
            )}
            <p className="whitespace-pre-wrap break-words px-1 text-[13px] leading-snug">
              {hasText ? (
                assembleTextSendMessage(body, paidForDisclaimer)
              ) : (
                <span className="text-muted-foreground">Your message appears here…</span>
              )}
              {typing && hasText && (
                <span
                  data-testid="typing-caret"
                  aria-hidden="true"
                  className="animate-express-caret ml-px inline-block h-[1.1em] w-[1.5px] translate-y-[0.15em] bg-primary"
                />
              )}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/** The preview with its caption, for the column beside a step. */
export function PhonePreviewAside(props: PhonePreviewProps) {
  return (
    <div>
      <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">What voters see</p>
      <PhonePreview {...props} />
    </div>
  );
}

/** The preview in a bottom sheet, for phones, where it can't sit beside the fields. */
export function PhonePreviewSheet({
  open,
  onOpenChange,
  ...content
}: TextSendContent & { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-10">
        <SheetHeader>
          <SheetTitle>What voters see</SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          <PhonePreview {...content} size="compact" />
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** On phones, where the preview can't sit beside the fields: one tap opens it. */
export function PhonePreviewButton(content: TextSendContent) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden">
      <Button type="button" variant="outline" className="w-full" onClick={() => setOpen(true)}>
        Preview
      </Button>
      <PhonePreviewSheet {...content} open={open} onOpenChange={setOpen} />
    </div>
  );
}
